import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchRushStatus,
  RUSH_INACTIVE,
  isScheduledClosure,
  formatReopenDate,
  type RushChannelStatus,
} from '../services/rushStatusApi';
import i18n from '../i18n';

const POLL_INTERVAL_MS = 30_000; // resync serveur toutes les 30s

interface UseRushStatusOptions {
  /** Désactive complètement le polling (ex: mode admin) */
  enabled?: boolean;
}

interface UseRushStatusResult {
  /** Le rush est-il actif côté serveur */
  isActive: boolean;
  /** Message personnalisé du restaurateur */
  message: string;
  /** Secondes restantes, décrémentées chaque seconde côté client */
  remainingSeconds: number;
  /** true = fermeture programmée (congés) → afficher le message seul, sans compteur */
  isScheduled: boolean;
  /** Date de réouverture formatée (congés), ou null si indisponible/inexploitable */
  reopenDate: string | null;
  /** Force un refresh immédiat (ex: après un refus create_order) */
  refresh: () => Promise<void>;
  /** Applique un statut reçu d'ailleurs (ex: refus create_order "rush_active") */
  applyStatus: (status: RushChannelStatus) => void;
}

/**
 * v5 — Hook du Mode Rush.
 *
 * - Appelle get_rush_status au montage puis toutes les 30s (polling).
 * - Décrémente remaining_seconds chaque seconde côté client (compteur fluide).
 * - Le polling resynchronise et corrige la dérive du compteur local.
 * - Quand le compteur atteint 0 → refresh immédiat pour savoir si le rush est levé.
 *
 * Sécurité : en cas d'erreur réseau, on ne bloque JAMAIS (fallback = inactif).
 */
export function useRushStatus(options: UseRushStatusOptions = {}): UseRushStatusResult {
  const { enabled = true } = options;

  const [status, setStatus] = useState<RushChannelStatus>(RUSH_INACTIVE);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const mountedRef = useRef(true);
  // Évite de déclencher plusieurs refresh quand le compteur touche 0
  const zeroRefreshRef = useRef(false);

  const applyStatus = useCallback((next: RushChannelStatus) => {
    setStatus(next);
    setRemainingSeconds(next.remaining_seconds);
    zeroRefreshRef.current = false;
  }, []);

  const refresh = useCallback(async () => {
    const result = await fetchRushStatus();
    if (!mountedRef.current) return;
    if (!result) {
      // Erreur technique → on ne bloque pas le client
      applyStatus(RUSH_INACTIVE);
      return;
    }
    applyStatus(result);
  }, [applyStatus]);

  // --- Polling serveur (30s) ---
  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) {
      setStatus(RUSH_INACTIVE);
      setRemainingSeconds(0);
      return;
    }

    refresh();
    const intervalId = setInterval(refresh, POLL_INTERVAL_MS);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [enabled, refresh]);

  // --- Compteur local (1s) ---
  useEffect(() => {
    if (!enabled || !status.active) return;

    const tickId = setInterval(() => {
      setRemainingSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          // Le compteur est arrivé à 0 → resync immédiate pour savoir
          // si le rush est réellement levé côté serveur.
          if (!zeroRefreshRef.current) {
            zeroRefreshRef.current = true;
            refresh();
          }
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(tickId);
  }, [enabled, status.active, refresh]);

  const scheduled = enabled && status.active && isScheduledClosure(status);
  return {
    isActive: enabled && status.active,
    message: status.message,
    remainingSeconds,
    isScheduled: scheduled,
    reopenDate: scheduled ? formatReopenDate(status.until, i18n.language || 'fr') : null,
    refresh,
    applyStatus,
  };
}
